import React, { useEffect } from 'react';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { TIPO_TRAMITE_OPTIONS } from '@/otros-tramites/constants/config';
import type { ContabilidadSettingsConfig } from '../types';

type Props = {
  settings: ContabilidadSettingsConfig;
  onChange: (next: ContabilidadSettingsConfig) => void;
  onValidationChange?: (error: string | null) => void;
};

const ContabilidadSettingsForm: React.FC<Props> = ({ settings, onChange, onValidationChange }) => {
  const fechaError = settings.pendientes_desde ? null : 'Indicá desde qué fecha se muestran los pendientes.';
  const tiposCertificaciones = settings.tipos_certificaciones_poderes ?? [];

  useEffect(() => {
    onValidationChange?.(fechaError);
  }, [fechaError, onValidationChange]);

  const toggleTipo = (tipo: string, incluido: boolean) => {
    onChange({
      ...settings,
      tipos_certificaciones_poderes: TIPO_TRAMITE_OPTIONS.filter((opcion) =>
        opcion === tipo ? incluido : tiposCertificaciones.includes(opcion),
      ),
    });
  };

  return (
    <div className="space-y-8">
      <div className="space-y-2 max-w-xs">
        <Label htmlFor="cont-pendientes-desde">Mostrar pendientes desde</Label>
        <Input
          id="cont-pendientes-desde"
          type="date"
          aria-invalid={!!fechaError}
          value={settings.pendientes_desde ?? ''}
          onChange={(event) => onChange({ ...settings, pendientes_desde: event.target.value || null })}
        />
        {fechaError && <p className="text-sm text-red-600">{fechaError}</p>}
        <p className="text-sm text-gray-500">
          Se listan los cobros y egresos pendientes con fecha igual o posterior a este día. Los
          anteriores quedan ocultos y no se suman en los KPIs del resumen; el presupuesto y los
          trámites no se modifican.
        </p>
      </div>

      <fieldset className="space-y-3">
        <legend className="text-sm font-medium">Fondo Certificaciones y poderes</legend>
        <p className="text-sm text-gray-500">
          Lo cobrado por los tipos marcados se reparte aparte, en el fondo Certificaciones y poderes. El
          resto de los otros trámites va a Escrituras.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {TIPO_TRAMITE_OPTIONS.map((tipo) => {
            const id = `cont-fondo-${tipo}`;
            return (
              <div key={tipo} className="flex items-center gap-2">
                <Checkbox
                  id={id}
                  checked={tiposCertificaciones.includes(tipo)}
                  onCheckedChange={(checked) => toggleTipo(tipo, checked === true)}
                />
                <Label htmlFor={id} className="font-normal">
                  {tipo}
                </Label>
              </div>
            );
          })}
        </div>
        <p className="text-sm text-gray-500">
          Los cambios se aplican a los cobros que se registren desde ahora; lo ya cobrado no cambia de fondo.
        </p>
      </fieldset>
    </div>
  );
};

export default ContabilidadSettingsForm;
